import * as pc from 'playcanvas';
import { spawnCharacter } from './character';
import type { WorldMap } from '../api/types';

/**
 * Ambient pedestrians: instances of the shared rigged character walking
 * ping-pong routes along each road's sidewalks. Pure ambience — no collision,
 * no interaction — but they turn "abandoned museum" into "living city".
 */

const WALK_SPEED = 1.35;
/** Keep clear of the central hub and the district plaza at each road's end. */
const T_MIN = 0.14;
const T_MAX = 0.72;

interface Walker {
  entity: pc.Entity;
  ax: number;
  az: number;
  dirX: number;
  dirZ: number;
  perpX: number;
  perpZ: number;
  offset: number;
  length: number;
  t: number;
  forward: 1 | -1;
}

export function spawnPedestrians(
  app: pc.Application,
  root: pc.Entity,
  map: WorldMap,
  playerPos: () => { x: number; z: number },
): void {
  const walkers: Walker[] = [];

  map.roads.forEach((road, i) => {
    const dx = road.to.x - road.from.x;
    const dz = road.to.z - road.from.z;
    const length = Math.hypot(dx, dz);
    const dirX = dx / length;
    const dirZ = dz / length;

    [-1, 1].forEach((side, j) => {
      void spawnCharacter(app).then((inst) => {
        if (!inst) return;
        inst.anim.speed = 1.35;
        root.addChild(inst.entity);
        walkers.push({
          entity: inst.entity,
          ax: road.from.x,
          az: road.from.z,
          dirX,
          dirZ,
          perpX: -dirZ,
          perpZ: dirX,
          offset: side * (road.width / 2 + 0.9),
          length,
          t: T_MIN + ((i * 0.19 + j * 0.31) % 1) * (T_MAX - T_MIN),
          forward: side < 0 ? 1 : -1,
        });
      });
    });
  });

  app.on('update', (dt: number) => {
    const p = playerPos();
    for (const w of walkers) {
      w.t += (w.forward * WALK_SPEED * dt) / w.length;
      if (w.t > T_MAX) {
        w.t = T_MAX;
        w.forward = -1;
      } else if (w.t < T_MIN) {
        w.t = T_MIN;
        w.forward = 1;
      }
      const x = w.ax + w.dirX * w.length * w.t + w.perpX * w.offset;
      const z = w.az + w.dirZ * w.length * w.t + w.perpZ * w.offset;
      w.entity.setPosition(x, 0.2, z); // sidewalk top sits ~0.2 above ground
      const hx = w.dirX * w.forward;
      const hz = w.dirZ * w.forward;
      w.entity.setEulerAngles(0, (Math.atan2(hx, hz) * 180) / Math.PI, 0);
      // Skinning is the expensive part — freeze the animation (not the motion)
      // for pedestrians too far away to read anyway.
      if (w.entity.anim) {
        w.entity.anim.enabled = Math.hypot(x - p.x, z - p.z) < 90;
      }
    }
  });
}
